import ServerTtsVoicePicker from '../ServerTtsVoicePicker';
import '../../i18n';
import React from 'react';
import styled from 'styled-components';
import {useTranslation} from 'react-i18next';
import {FiHeadphones, FiX, FiPlay, FiPause, FiZap} from 'react-icons/fi';

export default function AudioCreateContent({sources, source, onSourceChange, excerpt, voices = [], voicesLoading, voicesError, voice, onVoiceChange, onPreviewToggle, playing, previewing, generating, canContinue, onContinue, onClose, children}) {
    const {t} = useTranslation();
    const hasVoices = !voicesLoading && !voicesError && voices.length > 0;
    return <Frame>
        <Header>
            <Title><TitleIcon aria-hidden="true"><FiHeadphones size={19}/></TitleIcon>{t('messages:createAudio')}</Title>
            <Close type="button" aria-label={t('common:close')} onClick={onClose}><FiX size={20}/></Close>
        </Header>
        <Body>
            <Section>
                <SectionHeading>
                    <Label>{t('messages:textExcerpt')}</Label>
                    {sources?.length > 1 ? <SourceTabs role="group" aria-label={t('messages:titleNote')}>
                        {sources.map(option => <SourceTab key={option.id} type="button" aria-pressed={source === option.id}
                            disabled={generating || !option.text.trim()} onClick={() => onSourceChange(option.id)}>{option.label}</SourceTab>)}
                    </SourceTabs> : null}
                </SectionHeading>
                <Excerpt>{excerpt}</Excerpt>
            </Section>
            <Section>
                <Label>{t('messages:voice')}</Label>
                {voicesLoading || voicesError || !voices.length || typeof voices[0] === 'object' ?
                    <ServerTtsVoicePicker voices={voices} loading={voicesLoading} error={voicesError} voice={voice}
                        disabled={previewing || generating} playing={playing} onVoiceChange={onVoiceChange} onPreviewToggle={onPreviewToggle}/> :
                    <LegacyPills role="group" aria-label={t('messages:voice')}>
                        {voices.map(option => <LegacyPill key={option} type="button" aria-pressed={voice === option}
                            aria-busy={voice === option && previewing} disabled={previewing || generating}
                            onClick={() => voice === option ? onPreviewToggle() : onVoiceChange(option)}>
                            {voice === option ? playing ? <FiPause size={14}/> : <FiPlay size={14}/> : null}{option}
                        </LegacyPill>)}
                    </LegacyPills>}
                {hasVoices ? <Hint>{t('messages:tapAVoiceToPreviewItTap')}</Hint> : null}
            </Section>
            {children}
        </Body>
        <Footer>
            {hasVoices ? <UsageNote title={t('messages:audioCreationUsageHint')}><FiZap size={13} aria-hidden="true"/>{t('settings:aiUsage')}</UsageNote> : null}
            <Actions>
                <Secondary type="button" onClick={onClose}>{t('common:cancel')}</Secondary>
                <Primary type="button" onClick={onContinue} disabled={!hasVoices || !canContinue || previewing || generating}>
                    {generating ? t('messages:starting') : t('messages:createAudio')}
                </Primary>
            </Actions>
        </Footer>
    </Frame>;
}
const Frame = styled.div`
    display: flex; flex-direction: column; max-height: min(85vh, 680px); max-height: min(85dvh, 680px);
    color: rgba(255,255,255,.92); font-size: 14px;
`;
const Header = styled.div`display: flex; align-items: center; justify-content: space-between; padding: 22px 24px 18px; gap: 12px; flex-shrink: 0;`;
const Title = styled.h2`
    && {margin: 0; color: #f3f3ed; font-family: inherit; font-size: 19px; line-height: 26px; font-weight: 600;}
    display: flex; align-items: center; gap: 12px;
`;
const TitleIcon = styled.span`display: inline-flex; align-items: center; justify-content: center; color: #c5d5ca;`;
const Close = styled.button`
    display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0;
    width: 36px; height: 36px; border: 0; border-radius: 50%; background: rgba(255,255,255,.055);
    color: rgba(255,255,255,.65); cursor: pointer;
    &:hover {background: rgba(255,255,255,.1); color: white;}
    &:focus-visible {outline: 2px solid #b6c9bc; outline-offset: 2px;}
`;
const Body = styled.div`padding: 0 24px 24px; min-height: 0; overflow-y: auto; display: flex; flex-direction: column; gap: 24px;`;
const Section = styled.section`display: flex; flex-direction: column; gap: 10px; min-width: 0;`;
const SectionHeading = styled.div`display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 10px;`;
const Label = styled.div`font-size: 12px; font-weight: 500; color: rgba(255,255,255,.55);`;
const Excerpt = styled.div`
    padding: 14px 16px; border-radius: 12px; background: rgba(0,0,0,.14);
    border: 1px solid rgba(255,255,255,.07); color: rgba(255,255,255,.8); font-size: 14px; line-height: 1.6;
    overflow: hidden; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical;
    overflow-wrap: anywhere;
`;
const SourceTabs = styled.div`display: inline-flex; gap: 2px; padding: 3px; border-radius: 8px; background: rgba(0,0,0,.17);`;
const SourceTab = styled.button`
    border: 0; border-radius: 6px; padding: 5px 10px; font: inherit; font-size: 12px; cursor: pointer;
    background: ${({ 'aria-pressed': selected }) => selected ? 'rgba(255,255,255,.11)' : 'transparent'};
    color: ${({ 'aria-pressed': selected }) => selected ? 'rgba(255,255,255,.92)' : 'rgba(255,255,255,.5)'};
    &:disabled {opacity: .35; cursor: default;}
    &:focus-visible {outline: 2px solid #b6c9bc; outline-offset: 1px;}
`;
const LegacyPills = styled.div`display: flex; flex-wrap: wrap; gap: 8px;`;
const LegacyPill = styled.button`
    display: inline-flex; align-items: center; gap: 6px; border: 1px solid rgba(255,255,255,.16);
    border-radius: 999px; padding: 9px 14px; cursor: pointer;
    background: ${({ 'aria-pressed': selected }) => selected ? '#f3f3ed' : 'transparent'};
    color: ${({ 'aria-pressed': selected }) => selected ? '#222729' : 'rgba(255,255,255,.9)'};
    &:disabled {opacity: .45; cursor: default;}
`;
const Hint = styled.p`margin: 0; font-size: 12px; line-height: 1.5; color: rgba(255,255,255,.48);`;
const Footer = styled.div`
    border-top: 1px solid rgba(255,255,255,.07); padding: 16px 24px 22px; flex-shrink: 0;
    display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 12px;
`;
const UsageNote = styled.span`display: inline-flex; align-items: center; gap: 6px; font-size: 12px; color: rgba(255,255,255,.6);`;
const Actions = styled.div`display: flex; justify-content: flex-end; gap: 8px; margin-left: auto;`;
const Secondary = styled.button`
    min-height: 40px; border: 1px solid rgba(255,255,255,.13); border-radius: 9px;
    padding: 9px 15px; background: transparent; color: rgba(255,255,255,.7); cursor: pointer; font: inherit;
    &:focus-visible {outline: 2px solid #b6c9bc; outline-offset: 2px;}
`;
const Primary = styled(Secondary)`
    background: #f3f3ed; border-color: #f3f3ed; color: #222729; font-weight: 600; padding-inline: 18px;
    &:disabled {background: #394241; border-color: #394241; color: #aab3ad; opacity: 1; cursor: default;}
`;
